import { AttributeColumn, GroupAccessToColumn, OwnerTypeColumn } from '../components/reporting/GroupAccessOwners/BaseColumns';
import { IFilterable } from '../components/reporting/GroupAccessOwners/QueryableTableView';
import { IGroupAccessOwnerColumn, MakeSelectable } from '../models/GroupAccessOwners/GroupAccessOwnerColumn';
import { IQueryOptions } from '../models/GroupAccessOwners/ServiceInterfaces';
import { GroupAccessOwnerType } from '../models/IGroupAccessOwner';
import { HrisExpectedColumnType } from '../models/IHrisAttributeModel';
import { getHrisAttributeMappings, fetchGroupAccessOwners } from '../services/HierarchyAPI';
import { GroupAccessOwnerViewActionTypes, IBaseViewScope, IGroupAccessOwnerViewActionCreators } from './GroupAccessOwners/ViewStore';

export interface IProfileViewScope extends IBaseViewScope {
	forProfile: string;
	hierarchyIds: string[];
}

export const ActionCreators: IGroupAccessOwnerViewActionCreators<IProfileViewScope> = {
	constructColumns: (scope: IProfileViewScope, forView: string) => async (dispatch) => {
		// Dispatch to signify initiation of column construction
		dispatch({ type: GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_STARTED, forView });

		try {
			// Fetch prereq. attribute mappings
			const attributeMappings = await getHrisAttributeMappings(scope.forHris);

			// Construct columns
			const columns: IGroupAccessOwnerColumn[] = [
				...attributeMappings
					.filter((attr) => attr.property !== HrisExpectedColumnType.ManagerId)
					.map((attr) => {
						switch (attr.property) {
							case HrisExpectedColumnType.Name:
							case HrisExpectedColumnType.FirstName:
							case HrisExpectedColumnType.LastName:
								return MakeSelectable(AttributeColumn(attr), { selectedByDefault: true, selectionLocked: true });
							default:
								return MakeSelectable(AttributeColumn(attr), { selectedByDefault: true });
						}
					}),

				MakeSelectable(GroupAccessToColumn(), { selectedByDefault: true, selectionLocked: true }),
				MakeSelectable(OwnerTypeColumn(), {}),
			];

			// Dispatch to populate the store with the constructed columns
			dispatch({ type: GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FINISHED, columns, forView });
		} catch {
			// Dispatch to notify that columns could not be constructed
			dispatch({ type: GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FAILED, forView });
		}
	},

	constructFilterables: (scope: IProfileViewScope, forView: string) => async (dispatch) => {
		// Dispatch to signify initiation of filterable construction
		dispatch({ type: GroupAccessOwnerViewActionTypes.FILTERABLE_CONSTRUCTION_STARTED, forView });

		// Construct filterable for owner type
		const ownerTypeFilterable: IFilterable = {
			filterOnField: {
				boundToEntity: OwnerTypeColumn().asQueryable.boundToEntity,
				displayName: 'Owner Type',
				fieldName: OwnerTypeColumn().asQueryable.fieldName,
			},

			for: [
				{
					displayName: 'Employee',
					value: GroupAccessOwnerType.Employee,
				},
				{
					displayName: 'Collaborator',
					value: GroupAccessOwnerType.Collaborator,
				},
			],
		};

		// Dispatch to populate the store with the constructed filterables
		dispatch({
			type: GroupAccessOwnerViewActionTypes.FILTERABLE_CONSTRUCTION_FINISHED,
			filterables: [ownerTypeFilterable],
			forView,
		});
	},

	queryForOwners: (query: IQueryOptions, scope: IProfileViewScope, forView: string) => async (dispatch) => {
		// Dispatch to request people
		dispatch({ type: GroupAccessOwnerViewActionTypes.REQUEST_PEOPLE, forView });

		const profileQueryOptions: IQueryOptions = {
			...query,
			queryExpressions: [
				...(query.queryExpressions || []),
				{
					fieldName: 'TiedToProfileId',
					boundToEntity: 'GroupAccessOwner',
					queryOperation: 'FilterBy',
					values: [scope.forProfile],
				},
				{
					fieldName: 'HierarchyId',
					boundToEntity: 'HierarchyNodeOwnership',
					queryOperation: 'FilterBy',
					values: scope.hierarchyIds,
				},
			],
		};

		try {
			// Perform fetching of people
			const people = await fetchGroupAccessOwners(profileQueryOptions, scope.forHris);

			// Dispatch people fetched
			dispatch({ type: GroupAccessOwnerViewActionTypes.PEOPLE_FETCHED, people, forView });
		} catch {
			// Dispatch to notify that people could not be fetched
			dispatch({ type: GroupAccessOwnerViewActionTypes.PEOPLE_COULDNOT_BEFETCHED, forView });
		}
	},
};
